import React, { Component } from 'react';

class AddressInput extends Component {
    constructor(props) {
        super(props);
        this.state = {
            value: this.props.value || '',
            isInitialized: false,
            showCurrentLocationOption: false,
            isLoadingLocation: false
        };
        this.inputRef = null;
        this.autocomplete = null;
        this.lastSelectedAddress = null;
        this.blurTimeout = null;
        this.geocodeRequestId = 0;
    }

    componentDidMount() {
        this.initializeAutocomplete();
        document.addEventListener('click', this.handleClickOutside);
    }

    componentDidUpdate(prevProps) {
        if (prevProps.value !== this.props.value) {
            this.setState({ value: this.props.value || '' });
        }
    }

    componentWillUnmount() {
        if (this.autocomplete) {
            window.google.maps.event.clearInstanceListeners(this.autocomplete);
        }
        document.removeEventListener('click', this.handleClickOutside);
        if (this.blurTimeout) {
            clearTimeout(this.blurTimeout);
        }
        // Invalidate any in-flight geocode callbacks
        this.geocodeRequestId++;
    }

    initializeAutocomplete = () => {
        if (typeof google === 'undefined' || !google.maps || !google.maps.places) {
            setTimeout(this.initializeAutocomplete, 100);
            return;
        }

        if (!this.inputRef || this.state.isInitialized) return;

        const options = {
            types: ['geocode'],
            componentRestrictions: { country: 'nz' },
            fields: ['address_components', 'formatted_address', 'geometry', 'name', 'place_id']
        };

        this.autocomplete = new google.maps.places.Autocomplete(this.inputRef, options);
        this.autocomplete.addListener('place_changed', this.handlePlaceChanged);

        this.setState({ isInitialized: true });
    };

    handlePlaceChanged = () => {
        const place = this.autocomplete.getPlace();

        if (!place.geometry) return;

        // A proper selection supersedes any pending/in-flight blur geocode
        if (this.blurTimeout) {
            clearTimeout(this.blurTimeout);
            this.blurTimeout = null;
        }
        this.geocodeRequestId++;
        this.lastSelectedAddress = place.formatted_address;

        const lat = place.geometry.location.lat();
        const lng = place.geometry.location.lng();

        const addressData = {
            address: place.formatted_address,
            name: place.name,
            place_id: place.place_id,
            coordinates: { lat, lng },
            address_components: place.address_components
        };

        this.setState({ value: place.formatted_address });

        if (this.props.onChange) {
            this.props.onChange(addressData);
        }
    };

    handleInputChange = (e) => {
        const value = e.target.value;
        // User is editing manually, so the previous selection no longer applies
        if (value !== this.lastSelectedAddress) {
            this.lastSelectedAddress = null;
        }
        this.setState({
            value,
            showCurrentLocationOption: value.trim() === ''
        });

        if (!value && this.props.onChange) {
            this.props.onChange(null);
        }
    };

    handleFocus = () => {
        if (this.state.value.trim() === '') {
            this.setState({ showCurrentLocationOption: true });
        }
    };

    handleClickOutside = (e) => {
        if (this.inputRef && !this.inputRef.contains(e.target)) {
            this.setState({ showCurrentLocationOption: false });
        }
    };

    handleBlur = () => {
        if (this.blurTimeout) {
            clearTimeout(this.blurTimeout);
        }

        // Delay so a click on an autocomplete suggestion (which blurs the input
        // before place_changed fires) can cancel this geocode of the raw text
        this.blurTimeout = setTimeout(() => {
            this.blurTimeout = null;
            if (this.state.value && this.state.value !== this.lastSelectedAddress && !this.props.coordinates) {
                this.geocodeAddress(this.state.value);
            }
        }, 300);
    };

    geocodeAddress = (address) => {
        if (typeof google === 'undefined' || !google.maps) return;

        // Too broad to be useful; leave the typed text and filter untouched
        if (address.trim().toLowerCase() === 'new zealand') {
            return;
        }

        const requestId = ++this.geocodeRequestId;

        const geocoder = new google.maps.Geocoder();
        geocoder.geocode({
            address: address,
            componentRestrictions: { country: 'NZ' }
        }, (results, status) => {
            // A newer geocode or an autocomplete selection has superseded this request
            if (requestId !== this.geocodeRequestId) {
                return;
            }

            if (status === 'OK' && results[0]) {
                const result = results[0];

                if (result.formatted_address === 'New Zealand' || result.formatted_address.trim() === 'New Zealand') {
                    return;
                }

                const addressComponents = result.address_components || [];
                const hasSpecificLocation = addressComponents.some(component =>
                    component.types.includes('locality') ||
                    component.types.includes('administrative_area_level_2') ||
                    component.types.includes('sublocality') ||
                    component.types.includes('postal_town') ||
                    component.types.includes('route') ||
                    component.types.includes('street_number')
                );

                if (!hasSpecificLocation) {
                    return;
                }

                const lat = result.geometry.location.lat();
                const lng = result.geometry.location.lng();

                const addressData = {
                    address: result.formatted_address,
                    coordinates: { lat, lng },
                    address_components: result.address_components,
                    geocoded: true
                };

                this.lastSelectedAddress = result.formatted_address;
                this.setState({ value: result.formatted_address });

                if (this.props.onChange) {
                    this.props.onChange(addressData);
                }
            }
        });
    };

    useCurrentLocation = () => {
        this.setState({ isLoadingLocation: true });

        if (!navigator.geolocation) {
            alert('Geolocation is not supported by this browser.');
            this.setState({ isLoadingLocation: false, showCurrentLocationOption: false });
            return;
        }

        navigator.geolocation.getCurrentPosition(
            (position) => {
                this.reverseGeocodeCurrentLocation(position.coords.latitude, position.coords.longitude);
            },
            (error) => {
                let errorMessage = 'Unable to get your location. ';

                switch (error.code) {
                    case error.PERMISSION_DENIED:
                        errorMessage += 'Please allow location access and try again.';
                        break;
                    case error.POSITION_UNAVAILABLE:
                        errorMessage += 'Location information is unavailable.';
                        break;
                    case error.TIMEOUT:
                        errorMessage += 'Location request timed out.';
                        break;
                    default:
                        errorMessage += 'An unknown error occurred.';
                        break;
                }

                alert(errorMessage);
                this.setState({ isLoadingLocation: false, showCurrentLocationOption: false });
            },
            {
                enableHighAccuracy: true,
                timeout: 10000,
                maximumAge: 300000
            }
        );
    };

    reverseGeocodeCurrentLocation = (lat, lng) => {
        if (typeof google === 'undefined' || !google.maps) {
            setTimeout(() => this.reverseGeocodeCurrentLocation(lat, lng), 100);
            return;
        }

        const geocoder = new google.maps.Geocoder();
        geocoder.geocode({
            location: { lat, lng }
        }, (results, status) => {
            if (status === 'OK' && results[0]) {
                const result = results[0];

                const addressData = {
                    address: result.formatted_address,
                    coordinates: { lat, lng },
                    address_components: result.address_components,
                    currentLocation: true
                };

                this.lastSelectedAddress = result.formatted_address;
                this.setState({
                    value: result.formatted_address,
                    isLoadingLocation: false,
                    showCurrentLocationOption: false
                });

                if (this.props.onChange) {
                    this.props.onChange(addressData);
                }
            } else {
                alert('Unable to determine your address. Please enter it manually.');
                this.setState({ isLoadingLocation: false, showCurrentLocationOption: false });
            }
        });
    };

    clearInput = () => {
        this.lastSelectedAddress = null;
        // Cancel any pending/in-flight geocode so it can't repopulate the cleared input
        if (this.blurTimeout) {
            clearTimeout(this.blurTimeout);
            this.blurTimeout = null;
        }
        this.geocodeRequestId++;
        this.setState({ value: '' });
        if (this.props.onChange) {
            this.props.onChange(null);
        }
    };

    render() {
        const { placeholder, style, disabled } = this.props;
        const { value, showCurrentLocationOption, isLoadingLocation } = this.state;

        return (
            <div style={{ position: 'relative', width: '100%', ...style }}>
                <input
                    ref={(ref) => { this.inputRef = ref; }}
                    type="text"
                    value={value}
                    onChange={this.handleInputChange}
                    placeholder={placeholder || "Enter location (e.g. Auckland, Wellington)"}
                    disabled={disabled}
                    style={{
                        width: '100%',
                        padding: '14px',
                        border: '2px solid #e8eaed',
                        borderRadius: '10px',
                        fontSize: '14px',
                        transition: 'border-color 0.2s ease',
                        outline: 'none',
                        ...this.props.inputStyle
                    }}
                    onFocus={(e) => {
                        e.target.style.borderColor = '#ff8551';
                        this.handleFocus();
                    }}
                    onBlur={(e) => {
                        e.target.style.borderColor = '#e8eaed';
                        this.handleBlur();
                    }}
                />

                {/* Current Location Option */}
                {showCurrentLocationOption && (
                    <div style={{
                        position: 'absolute',
                        top: '100%',
                        left: 0,
                        right: 0,
                        background: 'white',
                        borderRadius: '8px',
                        boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)',
                        border: 'none',
                        marginTop: '2px',
                        fontFamily: 'Inter, sans-serif',
                        zIndex: 1000
                    }}>
                        <div
                            onClick={this.useCurrentLocation}
                            style={{
                                padding: '12px 16px',
                                cursor: isLoadingLocation ? 'default' : 'pointer',
                                fontSize: '14px',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '10px',
                                borderRadius: '8px',
                                transition: 'background-color 0.2s ease',
                                pointerEvents: isLoadingLocation ? 'none' : 'auto',
                                color: isLoadingLocation ? '#999' : '#333'
                            }}
                            onMouseEnter={(e) => {
                                if (!isLoadingLocation) {
                                    e.target.style.backgroundColor = '#f8f9fa';
                                }
                            }}
                            onMouseLeave={(e) => {
                                e.target.style.backgroundColor = 'white';
                            }}
                        >
                            <span style={{ fontSize: '16px' }}>📍</span>
                            {isLoadingLocation ? (
                                <span style={{ fontStyle: 'italic', color: '#999' }}>Getting location...</span>
                            ) : (
                                <span style={{ fontWeight: '500' }}>Use my current location</span>
                            )}
                        </div>
                    </div>
                )}

                {value && (
                    <button
                        type="button"
                        onClick={this.clearInput}
                        style={{
                            position: 'absolute',
                            right: '12px',
                            top: '50%',
                            transform: 'translateY(-50%)',
                            background: 'none',
                            border: 'none',
                            color: '#999',
                            cursor: 'pointer',
                            fontSize: '16px',
                            padding: '4px'
                        }}
                        title="Clear location"
                    >
                        ×
                    </button>
                )}
            </div>
        );
    }
}

export default AddressInput;
